import { useState, useEffect } from 'react';
import type { Organization } from '@hotjar/organization';
import type { SiteWithPriceInfo } from '@hotjar/webapp/domains/revenue/site/types';

export interface OrganizationsListItem extends Organization {
  sites: SiteWithPriceInfo[];
}

interface Props {
  filteredList: Array<OrganizationsListItem>;
  clear: () => void;
}

export function useOrganizationFilter(
  itemsList: Array<OrganizationsListItem>,
  filterString: string,
): Props {
  const [filteredList, setFilteredList] = useState(itemsList);

  const clear = () => {
    setFilteredList(itemsList);
  };

  const lowercasedFilterString = filterString.toLowerCase();

  useEffect(() => {
    const filter = () => {
      return itemsList
        .reduce((acc: Array<OrganizationsListItem>, organization: OrganizationsListItem) => {
          const filteredSites = organization.sites.filter(
            (site) =>
              site.name.toLocaleLowerCase().includes(lowercasedFilterString) ||
              site.url.toLocaleLowerCase().includes(lowercasedFilterString),
          );
          const organizationMatches = organization.name
            .toLocaleLowerCase()
            .includes(lowercasedFilterString);

          if (organizationMatches && !filteredSites.length) {
            return [...acc, { ...organization }];
          }
          if (filteredSites.length) {
            return [...acc, { ...organization, sites: filteredSites }];
          }

          return acc;
        }, [])
        .sort((a, b) => (a.name > b.name ? 1 : -1));
    };
    setFilteredList(filter());
  }, [itemsList, lowercasedFilterString]);

  return {
    filteredList,
    clear,
  };
}
